/**
 * TestDataManager Component - Extracted from Settings.jsx
 * Manages test data: view stats, clear test data, factory reset
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { FlaskConical, AlertTriangle, Loader2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';
import axios from 'axios';
import { BACKEND_URL } from '@/lib/api';

const API = `${BACKEND_URL}/api`;

const TestDataManager = ({ onRefresh }) => {
  const [testDataStats, setTestDataStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearingTestData, setClearingTestData] = useState(false);
  const [factoryResetting, setFactoryResetting] = useState(false);
  const [clearTestDataDialog, setClearTestDataDialog] = useState(false);
  const [factoryResetDialog, setFactoryResetDialog] = useState(false);
  const [factoryResetConfirm, setFactoryResetConfirm] = useState('');

  useEffect(() => {
    fetchTestDataStats();
  }, []);

  const fetchTestDataStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/test-data/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestDataStats(response.data);
    } catch (error) {
      console.error('Failed to fetch test data stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearTestData = async () => {
    setClearingTestData(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/test-data/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cleared ${response.data.total_deleted} test records`);
      setClearTestDataDialog(false);
      fetchTestDataStats();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setClearingTestData(false);
    }
  };

  const handleFactoryReset = async () => {
    if (factoryResetConfirm !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }
    
    setFactoryResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/test-data/factory-reset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Factory reset complete. Cleared ${response.data.total_deleted} records from ${response.data.collections_cleared} collections.`);
      setFactoryResetDialog(false);
      setFactoryResetConfirm('');
      fetchTestDataStats();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFactoryResetting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading test data statistics...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center" style={{ fontFamily: 'Space Grotesk' }}>
            <FlaskConical className="w-5 h-5 mr-2" />
            Test Data Management
          </CardTitle>
          <CardDescription>
            View and manage test data created during development and testing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Test Data Stats */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center justify-between">
              Test Data Summary
              <Button variant="ghost" size="sm" onClick={fetchTestDataStats}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </h3>
            {testDataStats ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(testDataStats.by_collection || {}).map(([collection, count]) => (
                  <div key={collection} className="p-3 border rounded-lg bg-gray-50">
                    <p className="text-xs text-gray-500 uppercase">{collection}</p>
                    <p className="text-xl font-bold text-purple-600">{count}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No test data statistics available</p>
            )}
            {testDataStats && (
              <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="font-medium text-purple-800">
                  Total Test Records: {testDataStats.total_count || 0}
                </p>
              </div>
            )}
          </div>

          {/* How Test Mode Works */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">How Test Mode Works</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Test data is flagged with <Badge variant="outline">is_test: true</Badge></li>
              <li>• Test dates use year 2099</li>
              <li>• Test names use &quot;TEST_&quot; prefix</li>
              <li>• All reports exclude test data by default</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={() => setClearTestDataDialog(true)}
              disabled={!testDataStats || testDataStats.total_count === 0}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Test Data Only
            </Button>
            <Button
              variant="outline"
              className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => setFactoryResetDialog(true)}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Factory Reset (Clear All Data)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clear Test Data Dialog */}
      <AlertDialog open={clearTestDataDialog} onOpenChange={setClearTestDataDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Test Data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all records flagged as test data across all collections.
              Production data will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearTestData} 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={clearingTestData}
            >
              {clearingTestData ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Clearing...</>
              ) : (
                'Clear Test Data'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Factory Reset Dialog */}
      <AlertDialog open={factoryResetDialog} onOpenChange={(open) => {
        setFactoryResetDialog(open);
        if (!open) setFactoryResetConfirm('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Factory Reset - Delete ALL Data?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block">
                This will <strong className="text-red-600">PERMANENTLY DELETE ALL DATA</strong> including:
              </span>
              <ul className="list-disc pl-5 space-y-1">
                <li>All day closings and deductions</li>
                <li>All purchase bills and payments</li>
                <li>All bank transactions</li>
                <li>All stock entries and inventory</li>
                <li>All price master items and recipes</li>
                <li>All payroll records</li>
                <li>All crockery inventory and movements</li>
              </ul>
              <span className="block text-red-600 font-medium">
                User accounts will be preserved.
              </span>
              <span className="block mt-4">
                Type <strong>RESET</strong> to confirm:
              </span>
              <Input
                value={factoryResetConfirm}
                onChange={(e) => setFactoryResetConfirm(e.target.value)}
                placeholder="Type RESET"
                className="mt-2"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleFactoryReset} 
              className="bg-red-600 hover:bg-red-700"
              disabled={factoryResetting || factoryResetConfirm !== 'RESET'}
            >
              {factoryResetting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting...</>
              ) : (
                'Factory Reset'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TestDataManager;
